import { useEffect, useState, useRef } from "react";

import useMediaSession from "./useMediaSession";
import useAudioProgress from "../hooks/useAudioProgress";

import MiniPlayerUI from "../ui/MiniPlayerUI";

import getListen from "../api/get/getListen";

const MiniPlayer = ({ bookId, onExpandChange, isSongTab }) => {
    const [audioList, setAudioList] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [bookInfo, setBookInfo] = useState({ imageUrl: "", author: "", title: "" });
    const [expanded, setExpanded] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef(new Audio());

    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);

    useAudioProgress(audioRef, bookId, currentIndex);

    useEffect(() => {
        const fetchAudioData = async () => {
            if (bookId) {
                try {
                    const bookData = await getListen(bookId);
                    if (bookData) {
                        const { audioUrls, audioFileNames, imageUrl, author, title } = bookData;
                        if (audioUrls && audioFileNames) {
                            setAudioList(audioUrls.map((url, index) => ({
                                name: audioFileNames[index],
                                url,
                            })));
                        }
                        setBookInfo({ imageUrl, author, title });
                    }
                } catch (error) {
                    console.error("getListen арқылы аудио деректерді алу кезінде қате:", error);
                }
            }
        };

        fetchAudioData();
    }, [bookId]);

    useEffect(() => {
        if (audioList.length > 0) {
            const audio = audioRef.current;
            const wasPlaying = !audio.paused;
            audio.src = audioList[currentIndex]?.url;

            if (wasPlaying) {
                audio.play();
            }
        }
    }, [currentIndex, audioList]);

    useEffect(() => {
        const audioElement = audioRef.current;
        let animationFrameId;

        const updateProgress = () => {
            if (audioElement.duration) {
                setCurrentTime(audioElement.currentTime);
                setDuration(audioElement.duration);
                setProgress((audioElement.currentTime / audioElement.duration) * 100);
            }
            animationFrameId = requestAnimationFrame(updateProgress);
        };

        audioElement.addEventListener("play", () => {
            animationFrameId = requestAnimationFrame(updateProgress);
        });

        audioElement.addEventListener("pause", () => {
            cancelAnimationFrame(animationFrameId);
        });

        return () => {
            cancelAnimationFrame(animationFrameId);
            audioElement.removeEventListener("play", updateProgress);
            audioElement.removeEventListener("pause", updateProgress);
        };
    }, []);

    useEffect(() => {
        onExpandChange?.(expanded);
    }, [expanded, onExpandChange]);

    const handlePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise.catch((error) => {
                    console.log("Аудионы ойнату қатесі:", error);
                });
            }
        }

        setIsPlaying(!isPlaying);
    };

    const handleNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex < audioList.length - 1 ? prevIndex + 1 : 0
        );
        setIsPlaying(true);
    };

    const handlePrev = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex > 0 ? prevIndex - 1 : audioList.length - 1
        );
        setIsPlaying(true);
    };

    const handlePlay = (index) => {
        setCurrentIndex(index);
        setIsPlaying(true);
        audioRef.current.play();
    };

    const toggleExpand = () => {
        setExpanded((prev) => !prev);
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };

    const handleSeek = (e) => {
        const newTime = (e.target.value / 100) * duration;
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const handleTouchStart = (e) => {
        setTouchStartX(e.touches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEndX(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStartX || !touchEndX || expanded) return;

        const deltaX = touchStartX - touchEndX;
        if (Math.abs(deltaX) > 50) {
            if (deltaX > 0) {
                handleNext();
            } else {
                handlePrev();
            }
        }

        setTouchStartX(null);
        setTouchEndX(null);
    };

    useMediaSession({
        title: audioList[currentIndex]?.name || "Аудио қолжетімсіз",
        artist: bookInfo.author || "Белгісіз автор",
        albumArt: bookInfo.imageUrl || "",
        isPlaying,
        onPlay: handlePlayPause,
        onPause: handlePlayPause,
        onNext: handleNext,
        onPrevious: handlePrev,
    });

    return (
        <MiniPlayerUI
            expanded={expanded}
            toggleExpand={toggleExpand}
            handleTouchStart={handleTouchStart}
            handleTouchMove={handleTouchMove}
            handleTouchEnd={handleTouchEnd}
            progress={progress}
            bookInfo={bookInfo}
            bookId={bookId}
            audioList={audioList}
            currentIndex={currentIndex}
            handlePlayPause={handlePlayPause}
            handlePlay={handlePlay}
            isPlaying={isPlaying}
            formatTime={formatTime}
            currentTime={currentTime}
            handleSeek={handleSeek}
            duration={duration}
            handlePrev={handlePrev}
            handleNext={handleNext}
            setCurrentTime={setCurrentTime}
            audioRef={audioRef}
            currentPlayingIndex={currentIndex}
            isSongTab={isSongTab}
        />
    );
};

export default MiniPlayer;